import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
// import { jwtDecode } from 'jwt-decode'; // 安全な手動実装に置き換え

/**
 * 権限チェック用カスタムフック
 * ユーザーの権限レベルに基づいてCRUD操作の可否を判定
 */
export const usePermission = () => {
  // TODO: UserContextの実装後に修正
  // const { user } = useContext(UserContext);
  const navigate = useNavigate();

  // JWT安全デコード関数（CLAUDE.md準拠）
  const getTokenData = () => {
    const token = localStorage.getItem('token');
    if (!token || typeof token !== 'string') {
      return null;
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT structure');
      }

      // Base64URLをBase64に変換してからデコード（パディング処理強化）
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const padding = base64.length % 4;
      const paddedBase64 = padding ? base64 + '='.repeat(4 - padding) : base64;

      const jsonPayload = decodeURIComponent(
        window
          .atob(paddedBase64)
          .split('')
          .map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      
      // JWT有効期限チェック
      if (decoded.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        const expiresAt = new Date(decoded.exp * 1000);
        const now = new Date(currentTime * 1000);
        const remainingSeconds = decoded.exp - currentTime;
        
        // デバッグログ出力（期限切れ時のみ）
        if (decoded.exp < currentTime) {
          console.warn('🔒 JWT expired - clearing token', {
            expiresAt: expiresAt.toISOString(),
            now: now.toISOString(),
            expiredAgo: `${Math.abs(remainingSeconds)} seconds ago`,
            tokenIat: decoded.iat ? new Date(decoded.iat * 1000).toISOString() : 'unknown',
          });
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return null;
        }
      }

      return decoded;
    } catch (error) {
      console.error('🔒 JWT decode error in usePermission:', error);
      // JWT decode error - fallback to guest permissions
      return null;
    }
  };

  const tokenData = getTokenData();

  // JWTトークンからユーザー情報を安全に取得
  const user = tokenData
    ? {
        id: tokenData.userId || tokenData.user_id || tokenData.id,
        user_id: tokenData.user_id || tokenData.userId || tokenData.id,
        role: tokenData.role || 'user',
        permission_level: tokenData.permission_level || 1,
        kycStatus: tokenData.kycStatus || 'pending',
        email: tokenData.email,
        username: tokenData.username,
      }
    : {
        permission_level: 1, // ゲストユーザーのデフォルト
      };

  /**
   * ユーザーの権限レベルを取得
   * @returns {number} 権限レベル（1:ゲスト, 2:準会員, 3:本会員, 4:管理者）
   */
  const getPermissionLevel = () => {
    // JWTトークンがない場合はゲスト
    if (!tokenData) {
      return 1;
    }

    // JWTトークンのpermission_levelを直接使用
    if (
      tokenData.permission_level &&
      typeof tokenData.permission_level === 'number'
    ) {
      return tokenData.permission_level;
    }

    // フォールバック: 管理者権限チェック
    if (tokenData.role === 'admin') {
      return 4;
    }

    // デフォルトはゲスト
    return 1;
  };

  /**
   * リソースの作成権限をチェック
   * @param {string} resource - リソース種別（question, project, store等）
   * @returns {boolean} 作成可能かどうか
   */
  const canCreate = (resource) => {
    const level = getPermissionLevel();

    // 管理者（レベル4）は全てのリソースを作成可能
    if (level >= 4) return true;

    switch (resource) {
      case 'question':
      case 'desire':
      case 'comment':
        return level >= 3; // 本会員以上（トークン消費機能）

      case 'resonance':
        return level >= 3; // 本会員以上（トークン拠出機能）

      case 'project':
      case 'store':
      case 'product':
      case 'dao':
      case 'proposal':
        return level >= 3; // 本会員のみ

      case 'friend_request':
      case 'chat_message':
        return level >= 2; // 準会員以上

      default:
        return false;
    }
  };

  /**
   * リソースの更新権限をチェック
   * @param {string} resource - リソース種別
   * @param {string} ownerId - リソースの所有者ID
   * @returns {boolean} 更新可能かどうか
   */
  const canUpdate = (resource, ownerId) => {
    const level = getPermissionLevel();

    if (level < 2) return false; // ゲストは更新不可

    // 管理者は全て更新可能
    if (level === 4) return true;

    // 準会員は自分のリソースのみ更新可能
    if (level === 2 && user.id !== ownerId) return false;

    // 本会員でも一部リソースは所有者のみ更新可能
    if (level === 3) {
      switch (resource) {
        case 'project':
        case 'store':
        case 'dao':
          return user.id === ownerId;
        default:
          return true;
      }
    }

    return false;
  };

  /**
   * リソースの削除権限をチェック
   * @param {string} resource - リソース種別
   * @param {string} ownerId - リソースの所有者ID
   * @returns {boolean} 削除可能かどうか
   */
  const canDelete = (resource, ownerId) => {
    const level = getPermissionLevel();

    // 管理者は全て削除可能
    if (level === 4) return true;

    if (level < 3) return false; // 本会員のみ削除可能

    // 自分のリソースのみ削除可能
    return user.id === ownerId;
  };

  /**
   * 特定のアクションが可能かチェック
   * @param {string} action - アクション種別（vote, purchase等）
   * @returns {boolean} アクション実行可能かどうか
   */
  const canPerform = (action) => {
    const level = getPermissionLevel();

    switch (action) {
      case 'vote':
        return level >= 3; // 本会員のみ
      case 'purchase':
        return level >= 2; // 準会員以上（一部制限あり）
      case 'resonance_view':
        return level >= 1; // ゲスト以上（閲覧のみ）
      default:
        return false;
    }
  };

  /**
   * 権限不足時のメッセージを取得
   * @param {string} action - 実行しようとしたアクション
   * @returns {string} エラーメッセージ
   */
  const getPermissionMessage = (action) => {
    const level = getPermissionLevel();

    if (level === 1) {
      return 'この機能を利用するにはアカウント登録が必要です。';
    }

    if (level === 2) {
      return 'この機能を利用するには本人確認（KYC）が必要です。';
    }

    if (level === 3) {
      return 'この機能を利用するには管理者権限が必要です。';
    }

    return '権限が不足しています。';
  };

  /**
   * 権限昇格画面への遷移
   */
  const navigateToUpgrade = () => {
    const level = getPermissionLevel();

    if (level === 1) {
      navigate('/register'); // 基本登録へ
    } else if (level === 2) {
      navigate('/kyc'); // KYC認証へ
    } else if (level === 3) {
      // 管理者権限が必要な場合は案内画面へ
      navigate('/contact'); // 管理者権限申請画面へ
    }
  };

  /**
   * リソースの閲覧権限をチェック
   * @param {string} resource - リソース種別
   * @returns {boolean} 閲覧可能かどうか
   */
  const canView = (resource) => {
    const level = getPermissionLevel();

    switch (resource) {
      case 'recruitment':
      case 'store':
      case 'project':
      case 'discussion':
        return level >= 1; // ゲストでも閲覧可能

      case 'wallet':
      case 'private_messages':
        return level >= 3; // 本会員以上

      case 'admin':
        return level >= 4; // 管理者のみ

      default:
        return level >= 1; // デフォルトはゲストでも閲覧可能
    }
  };

  /**
   * 指定された権限レベル以上かチェック
   * @param {number|string} requiredLevel - 必要な権限レベル
   * @returns {boolean} 権限があるかどうか
   */
  const hasPermission = (requiredLevel) => {
    // 文字列形式の権限レベルを数値に変換
    let numericRequiredLevel = requiredLevel;
    if (typeof requiredLevel === 'string') {
      const levelMap = {
        LEVEL_1: 1,
        LEVEL_2: 2,
        LEVEL_3: 3,
        LEVEL_4: 4,
      };
      numericRequiredLevel =
        levelMap[requiredLevel] || parseInt(requiredLevel) || 1;
    }

    return getPermissionLevel() >= numericRequiredLevel;
  };

  return {
    user,
    permissionLevel: getPermissionLevel(),
    hasPermission,
    canCreate,
    canUpdate,
    canDelete,
    canPerform,
    canView,
    getPermissionMessage,
    navigateToUpgrade,
  };
};
